import { useEffect, useMemo, useState } from 'react';
import { useDebouncedValue } from '@direct-frontend/stdlib';
import {
    type OpenApiGetPageContextResponseSuccess,
    type OpenApiSchemaPreviewCustomerPageContext,
} from '#shared/api/schema';
import { showError } from '#shared/lib/toast';
import { getConstructorPageContext } from '../api/page/api';
import {
    isConstructorPageAliasWithContext,
    mapStaticContextToPageContextRequest,
} from '../lib/mapStaticContextToPageContextRequest';

const DEBOUNCE_MS = 200;

interface UseResolvedConstructorPageContextParams {
    pageAlias?: string;
    staticContext?: unknown;
}

interface ResolvedPageContextState {
    requestKey: string;
    context: OpenApiSchemaPreviewCustomerPageContext;
}

export const useResolvedConstructorPageContext = ({
    pageAlias,
    staticContext,
}: UseResolvedConstructorPageContextParams): OpenApiSchemaPreviewCustomerPageContext | undefined => {
    const [resolved, setResolved] = useState<ResolvedPageContextState | undefined>();
    const debouncedStaticContext = useDebouncedValue(staticContext, DEBOUNCE_MS);
    const shouldResolve = isConstructorPageAliasWithContext(pageAlias);

    const request = useMemo(() => {
        if (!shouldResolve || !pageAlias) {
            return undefined;
        }

        return mapStaticContextToPageContextRequest(pageAlias, debouncedStaticContext);
    }, [shouldResolve, pageAlias, debouncedStaticContext]);

    const requestKey = request ? JSON.stringify(request) : undefined;

    useEffect(() => {
        if (!request || !requestKey) {
            return;
        }

        let cancelled = false;

        getConstructorPageContext(request)
            .then((response: OpenApiGetPageContextResponseSuccess) => {
                if (!cancelled) {
                    setResolved({ requestKey, context: response.context });
                }
            })
            .catch((error: unknown) => {
                if (!cancelled) {
                    setResolved(undefined);
                    showError({ label: 'Не удалось загрузить контекст страницы для превью' }, error);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [request, requestKey]);

    if (!requestKey || resolved?.requestKey !== requestKey) {
        return undefined;
    }

    return resolved.context;
};
